import { createFileRoute } from '@tanstack/react-router';
import {
  Button,
  Card,
  Divider,
  Empty,
  Flex,
  Form,
  Input,
  message,
  Modal,
  Tooltip,
  Typography,
} from 'antd';
import { getAuth, unlink, updatePassword, verifyBeforeUpdateEmail } from 'firebase/auth';
import { Lock, Mail, Unplug } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import {
  CREDENTIAL_TOO_OLD_USER_MESSAGE,
  isCredentialTooOldError,
} from '../../../../modules/auth/is-credential-too-old-error';
import { SettingsLayoutContent } from '../../../../ui/components/layout';
import { List } from '../../../../ui/components/list';
import { PasswordMeter } from '../../../../ui/components/password-meter';
import { Record } from '../../../../ui/components/record';
import { useUnsavedChangesGuard } from '../../../../ui/hooks';
import { GoogleIcon } from '../../../../ui/icons/google';
import { MicrosoftIcon } from '../../../../ui/icons/microsoft';
import { formatDateTime } from '../../../../ui/utils/format-date';

import type { ReactNode } from 'react';

const OAUTH_PROVIDER_CONFIG: Record<
  string,
  { label: string; providerId: string; icon: (props: { width?: number; height?: number }) => ReactNode }
> = {
  'google.com': {
    label: 'Google',
    providerId: 'google.com',
    icon: (props) => <GoogleIcon width={24} height={24} {...props} />,
  },
  'microsoft.com': {
    label: 'Microsoft',
    providerId: 'microsoft.com',
    icon: (props) => <MicrosoftIcon width={24} height={24} {...props} />,
  },
};

type ChangeEmailFormValues = {
  email: string;
};

type ChangePasswordFormValues = {
  password: string;
  passwordConfirmation: string;
};

type OAuthProviderEntry = {
  providerId: string;
  label: string;
  icon: ReactNode;
  providerAccountEmail: string;
  connectedAt: Date;
};

const SettingsSecurityRoute = () => {
  const [changeEmailForm] = Form.useForm<ChangeEmailFormValues>();
  const [changePasswordForm] = Form.useForm<ChangePasswordFormValues>();

  Form.useWatch([], changeEmailForm);
  Form.useWatch([], changePasswordForm);
  useUnsavedChangesGuard(
    () => changeEmailForm.isFieldsTouched() || changePasswordForm.isFieldsTouched(),
  );

  const [isSendingEmailChange, setIsSendingEmailChange] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [unlinkingProviderId, setUnlinkingProviderId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const { account } = Route.useRouteContext();
  const { confirm } = Modal;

  const refreshProviders = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  const signInMethods = useMemo(() => {
    const user = getAuth().currentUser;
    if (!user) {
      return {
        providers: [] as OAuthProviderEntry[],
        oauthProviders: [] as OAuthProviderEntry[],
        hasPassword: false,
        isEmailPasswordOnly: false,
        canDisconnectCount: 0,
      };
    }

    const providers = user.providerData
      .map((p) => {
        const config = OAUTH_PROVIDER_CONFIG[p.providerId];
        if (!config) {
          return null;
        }

        return {
          providerId: p.providerId,
          label: config.label,
          icon: config.icon({}),
          providerAccountEmail: p.email ?? user.email ?? '',
          connectedAt: user.metadata.creationTime
            ? new Date(user.metadata.creationTime)
            : new Date(),
        };
      })
      .filter((p): p is OAuthProviderEntry => p !== null);

    const hasPassword = user.providerData.some((p) => p.providerId === 'password');
    const isEmailPasswordOnly =
      hasPassword && user.providerData.every((p) => p.providerId === 'password');
    const canDisconnectCount = providers.length > 1 ? providers.length : 0;

    return {
      providers,
      oauthProviders: providers,
      hasPassword,
      isEmailPasswordOnly,
      canDisconnectCount,
    };
    // refreshKey intentionally triggers re-computation when providers are refreshed (unlink/updatePassword)
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUnlinkProvider = useCallback(
    (providerId: string) => {
      const user = getAuth().currentUser;
      if (!user) {
        message.error('Please sign in again and retry.');
        return;
      }

      if (signInMethods.providers.length <= 1) {
        message.error('You must have at least one sign-in method.');
        return;
      }

      const config = OAUTH_PROVIDER_CONFIG[providerId];
      const providerLabel = config?.label ?? providerId;

      confirm({
        title: `Disconnect ${providerLabel}?`,
        content: `You will no longer be able to sign in with ${providerLabel}. Make sure you can sign in with another method before disconnecting.`,
        okText: 'Disconnect',
        okButtonProps: { danger: true },
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            setUnlinkingProviderId(providerId);
            await unlink(user, providerId);
            message.success(`${providerLabel} disconnected.`);
            refreshProviders();
          } catch (error) {
            console.error('Error unlinking provider:', error);
            message.error('Failed to disconnect. Please try again.');
          } finally {
            setUnlinkingProviderId(null);
          }
        },
      });
    },
    [confirm, refreshProviders, signInMethods.providers.length],
  );

  const handleChangeEmail = async (values: ChangeEmailFormValues) => {
    const user = getAuth().currentUser;
    if (!user) {
      message.error('Please sign in again and retry.');
      return;
    }

    const nextEmail = values.email.trim().toLowerCase();
    if (nextEmail === user.email?.trim().toLowerCase()) {
      message.info('This is already your current email address.');
      return;
    }

    try {
      setIsSendingEmailChange(true);
      await verifyBeforeUpdateEmail(user, nextEmail, {
        url: `${window.location.origin}/auth/action?mode=changeEmail`,
        handleCodeInApp: true,
      });

      message.success('Verification email sent. Confirm it to finish changing your email.');
      changeEmailForm.resetFields();
    } catch (error) {
      console.error('Error sending email change verification:', error);
      if (isCredentialTooOldError(error)) {
        message.error(CREDENTIAL_TOO_OLD_USER_MESSAGE);
      } else {
        message.error('Failed to start email change. Please try again.');
      }
    } finally {
      setIsSendingEmailChange(false);
    }
  };

  const handleChangePassword = async (values: ChangePasswordFormValues) => {
    const user = getAuth().currentUser;
    if (!user) {
      message.error('Please sign in again and retry.');
      return;
    }

    try {
      setIsChangingPassword(true);
      await updatePassword(user, values.password);
      message.success(isSetPassword ? 'Password set' : 'Password changed.');
      changePasswordForm.resetFields();
      refreshProviders();
    } catch (error) {
      console.error('Error changing password:', error);
      if (isCredentialTooOldError(error)) {
        message.error(CREDENTIAL_TOO_OLD_USER_MESSAGE);
      } else {
        message.error('Failed to change password. Please try again.');
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const { hasPassword, isEmailPasswordOnly, oauthProviders, canDisconnectCount } = signInMethods;

  const isOAuthUser = oauthProviders.length > 0;
  const penalizedTokens = [account.firstName, account.lastName, account.email].filter(Boolean);
  const isSetPassword = isOAuthUser && !hasPassword;

  return (
    <SettingsLayoutContent
      title="Security"
      description="Manage your sign-in methods and account security."
    >
      <Flex vertical gap={16}>
        {isOAuthUser && (
          <>
            {oauthProviders.length === 0 ? (
              <Empty description="No connected apps" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <List
                items={oauthProviders}
                renderItem={({ providerId, icon, providerAccountEmail, connectedAt }) => ({
                  key: providerId,
                  reactNode: (
                    <Record
                      left={icon}
                      title={providerAccountEmail}
                      description={`Linked ${formatDateTime(connectedAt)}`}
                      right={
                        canDisconnectCount > 0 ? (
                          <Tooltip title="Disconnect">
                            <Button
                              color="danger"
                              variant="text"
                              icon={<Unplug size="1em" />}
                              size="small"
                              loading={unlinkingProviderId === providerId}
                              onClick={() => handleUnlinkProvider(providerId)}
                            />
                          </Tooltip>
                        ) : null
                      }
                    />
                  ),
                })}
              />
            )}
            <Divider dashed />
          </>
        )}

        {isEmailPasswordOnly && (
          <>
            <Card title="Change Email Address">
              <Typography.Paragraph type="secondary">
                Current email: <Typography.Text strong>{account.email}</Typography.Text>
              </Typography.Paragraph>

              <Form
                form={changeEmailForm}
                layout="vertical"
                validateTrigger="onSubmit"
                onFinish={handleChangeEmail}
              >
                <Form.Item
                  name="email"
                  label="New email address"
                  rules={[
                    { required: true, message: 'Please enter a new email address' },
                    { type: 'email', message: 'Please enter a valid email address' },
                  ]}
                >
                  <Input size="large" placeholder="name@company.com" prefix={<Mail size="1em" />} />
                </Form.Item>

                <Flex justify="flex-end">
                  <Button type="primary" htmlType="submit" loading={isSendingEmailChange}>
                    Send verification link
                  </Button>
                </Flex>
              </Form>
            </Card>
            <Divider dashed />
          </>
        )}

        <Card title={isSetPassword ? 'Set Password' : 'Change Password'}>
          {isSetPassword && (
            <Typography.Paragraph type="secondary">
              Set a password to sign in with email and password. Your email (
              <Typography.Text strong>{account.email}</Typography.Text>) is already verified from
              your connected provider.
            </Typography.Paragraph>
          )}

          <Form
            form={changePasswordForm}
            layout="vertical"
            validateTrigger="onSubmit"
            onFinish={handleChangePassword}
          >
            <Form.Item
              name="password"
              label={isSetPassword ? 'Password' : 'New password'}
              rules={[
                {
                  required: true,
                  message: isSetPassword
                    ? 'Please enter a password'
                    : 'Please enter a new password',
                },
                { min: 12, message: 'Password must be at least 12 characters long' },
              ]}
            >
              <Input.Password
                size="large"
                placeholder={isSetPassword ? 'Password' : 'New password'}
                prefix={<Lock size="1em" />}
              />
            </Form.Item>

            <Form.Item noStyle dependencies={['password']}>
              {({ getFieldValue }) => {
                const password = getFieldValue('password')?.trim() ?? '';

                return password ? (
                  <PasswordMeter password={password} penalizedTokens={penalizedTokens} />
                ) : null;
              }}
            </Form.Item>

            <Form.Item
              name="passwordConfirmation"
              label={isSetPassword ? 'Confirm your password' : 'Confirm new password'}
              dependencies={['password']}
              rules={[
                {
                  required: true,
                  message: isSetPassword
                    ? 'Please confirm your password'
                    : 'Please confirm your new password',
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }

                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password
                size="large"
                placeholder="Confirm password"
                prefix={<Lock size="1em" />}
              />
            </Form.Item>

            <Flex justify="flex-end">
              <Button
                type="primary"
                htmlType="submit"
                loading={isChangingPassword}
              >
                {isSetPassword ? 'Set password' : 'Update password'}
              </Button>
            </Flex>
          </Form>
        </Card>
      </Flex>
    </SettingsLayoutContent>
  );
};

export const Route = createFileRoute('/_authenticated/_app/settings/security')({
  component: SettingsSecurityRoute,
});
